import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useContext, useEffect, useState } from "react";
import { LanguageContext } from "context";

import JapanSVG from "assets/maps/japan.svg?react";

import "./JapanMap.css";
import type { CompletedPrefecture } from "@shared/api/dashboard";

import DefaultInfoCard from "../../parts/Cards/InfoCards/DefaultInfoCard";

import ColorInfoCard from "../../parts/Cards/ColorInfoCard";

const inProgressColor = "info";
const completedColor = "success";
interface JapanMapProps {
  completedPrefectures?: CompletedPrefecture[];
  inProgressPrefectures?: CompletedPrefecture[];
}

const prefectureNames: Record<string, string> = {
  "JP-01": "北海道",
  "JP-02": "青森県",
  "JP-03": "岩手県",
  "JP-04": "宮城県",
  "JP-05": "秋田県",
  "JP-06": "山形県",
  "JP-07": "福島県",
  "JP-08": "茨城県",
  "JP-09": "栃木県",
  "JP-10": "群馬県",
  "JP-11": "埼玉県",
  "JP-12": "千葉県",
  "JP-13": "東京都",
  "JP-14": "神奈川県",
  "JP-15": "新潟県",
  "JP-16": "富山県",
  "JP-17": "石川県",
  "JP-18": "福井県",
  "JP-19": "山梨県",
  "JP-20": "長野県",
  "JP-21": "岐阜県",
  "JP-22": "静岡県",
  "JP-23": "愛知県",
  "JP-24": "三重県",
  "JP-25": "滋賀県",
  "JP-26": "京都府",
  "JP-27": "大阪府",
  "JP-28": "兵庫県",
  "JP-29": "奈良県",
  "JP-30": "和歌山県",
  "JP-31": "鳥取県",
  "JP-32": "島根県",
  "JP-33": "岡山県",
  "JP-34": "広島県",
  "JP-35": "山口県",
  "JP-36": "徳島県",
  "JP-37": "香川県",
  "JP-38": "愛媛県",
  "JP-39": "高知県",
  "JP-40": "福岡県",
  "JP-41": "佐賀県",
  "JP-42": "長崎県",
  "JP-43": "熊本県",
  "JP-44": "大分県",
  "JP-45": "宮崎県",
  "JP-46": "鹿児島県",
  "JP-47": "沖縄県",
};

function JapanMap({ completedPrefectures, inProgressPrefectures }: JapanMapProps): JSX.Element {
  const { t } = useContext(LanguageContext)  as any;
  const [tooltip, setTooltip] = useState<{ name: string } | null>(null);

  useEffect(() => {
    if (!completedPrefectures && !inProgressPrefectures) return;

    document.querySelectorAll(".japan-map path").forEach((el) => {
      el.classList.remove("jp-active", "jp-in-progress");
    });

    completedPrefectures?.forEach((pref) => {
      const group = document.getElementById(`JP-${pref.id.toString().padStart(2, "0")}`);
      if (!group) return;
      const path = group.querySelector("path");
      if (path) {
        path.classList.add("jp-active");
      }
    });

    inProgressPrefectures?.forEach((pref) => {
      const group = document.getElementById(`JP-${pref.id.toString().padStart(2, "0")}`);
      if (!group) return;
      const path = group.querySelector("path");
      if (path) {
        path.classList.add("jp-in-progress");
      }
    });

    const svg = document.querySelector(".japan-map") as SVGSVGElement | null;
    if (!svg) return;

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as SVGPathElement;
      const parentGroup = target.closest("g");
      const id = parentGroup?.id;

      if (!id || !id.startsWith("JP-")) return;
      const name = prefectureNames[id];
      if (!name) return;
      setTooltip({ name });      
    };

    const handleMouseOut = () => {
        setTooltip(null);
    };

    svg.addEventListener("mouseover", handleMouseOver);
    svg.addEventListener("mousemove", handleMouseOver);
    svg.addEventListener("mouseout", handleMouseOut);
    return () => {
      svg.removeEventListener("mouseover", handleMouseOver);
      svg.removeEventListener("mousemove", handleMouseOver);
      svg.removeEventListener("mouseout", handleMouseOut);
    };
  }, [completedPrefectures, inProgressPrefectures]);

  return (
    <Card sx={{ width: "100%", minHeight: "auto", display: "flex", flexDirection: "column" }}>
      <MDBox display="flex">
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          variant="gradient"
          bgColor="success"
          color="white"
          shadow="md"
          borderRadius="xl"
          ml={3}
          mt={-2}
        >
          <Icon fontSize="medium" color="inherit">language</Icon>
        </MDBox>
        <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
          {t("JapanMap.title")}
        </MDTypography>
      </MDBox>

      <MDBox
        flex={1}
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="center"
        alignItems="flex-start"
        position="relative"
        sx={{ py: 2, px: { xs: 1, sm: 2, md: 4 }, gap: 2 }}
      >
        <MDBox sx={{ flexShrink: 0, width: { xs: "100%", md: 200 } }}>
          <DefaultInfoCard
            color="info"
            icon="map"
            title={t("JapanMap.prefecture")}
            value={`${completedPrefectures?.length ?? 0}/47`}
            description={t("JapanMap.acquisitions")}
          />
        </MDBox>

        <MDBox
          sx={{
            flex: 1,
            maxWidth: "960px",
            minHeight: { xs: "360px", sm: "500px", md: "600px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            position: "relative",
            width: "100%",
          }}
        >
          <MDBox
            mt={1}
            mb={1}
            width="100%"
            display="flex"
            justifyContent="flex-end"
            px={{ xs: 2, md: 4 }}
          >
            <ColorInfoCard
              color="dark"
              icon="check_circle"
              title={t("JapanMap.Legend")}
              badges={[
                { color: completedColor, size: "lg", badgeContent: t("JapanMap.acquired") },
                { color: inProgressColor, size: "lg", badgeContent: t("JapanMap.current-location") },
              ]}
            />
          </MDBox>

          <MDBox
            sx={{
              flex: 1,
              width: "100%",
              height: "100%",
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {tooltip && (
              <MDBox
                sx={{
                  position: "absolute",
                  top: "10%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.75)",
                  color: "#fff",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontSize: { xs: "1rem", sm: "1.5rem", md: "1.75rem" },
                  pointerEvents: "none",
                  zIndex: 2,
                  whiteSpace: "nowrap",
                }}
              >
                {tooltip.name}
              </MDBox>
            )}
            <JapanSVG
              className="japan-map"
              style={{
                width: "auto",
                height: "100%",
                maxHeight: "100%",
                display: "block",
                marginTop: 0,
                marginBottom: 0,
              }}
            />
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default JapanMap;